import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Page} from '../types/Page';
import {Section} from '../../artisticobjects/types/Section';
import pagesService from '../services/pagesService';
import {handleError} from '../../../common/utils/errorHandling';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {
  AdminButton,
  AdminForm,
  AdminFormSection,
  AdminInputText,
  AdminRichTextArea,
  FieldLanguageSwitcher
} from "../../../layout/admin";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";
import {DictionaryType} from '../../core/types/DictionaryItem';
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import {getTranslatedLanguages} from '../../../common/types/StringTranslations';

const CONTENT_SECTION_ALIAS = 'CNTN';

const PageForm: React.FC = () => {
  const {t} = useTranslation();
  const titleField = useTranslatableField();
  const contentField = useTranslatableField();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [formData, setFormData] = useState({
    alias: ''
  });
  const [page, setPage] = useState<Page | undefined>();
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();

  useEffect(() => {
    if (id) {
      fetchPage(id);
    }
  }, [id]);

  const fetchPage = async (pageId: string): Promise<void> => {
    await handleError(
      async () => {
        const pageData = await pagesService.getPage(pageId);
        setPage(pageData);
        titleField.setTranslations(pageData.title);
        setFormData({alias: pageData.alias});
        initializeContentFromSections(pageData.sections || []);
      },
      t('admin.pages.errors.fetching')
    );
  };

  const initializeContentFromSections = (sections: Section[]): void => {
    const contentSections = sections.filter(s => s.type.alias === CONTENT_SECTION_ALIAS);
    const translations: Record<string, string> = {};
    contentSections.forEach(section => {
      translations[section.language] = section.content;
    });
    contentField.setTranslations(translations);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const buildSectionsForSubmission = (): Section[] => {
    const sections: Section[] = [];
    const translatedLanguages = getTranslatedLanguages(contentField.translations);
    translatedLanguages.forEach(language => {
      const content = contentField.translations[language];
      if (content && content.trim()) {
        const existingSection = page?.sections?.find(
          s => s.type.alias === CONTENT_SECTION_ALIAS && s.language === language
        );
        sections.push({
          id: existingSection?.id || crypto.randomUUID(),
          type: {type: DictionaryType.PAGE_SECTION, alias: CONTENT_SECTION_ALIAS, value: ''},
          content: content.trim(),
          language: language
        });
      }
    });
    return sections;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    clearErrors();

    try {
      const sections = buildSectionsForSubmission();
      if (page) {
        await pagesService.updatePage({
          id: page.id,
          alias: formData.alias,
          title: titleField.translations,
          sections
        });
      } else {
        await pagesService.createPage({
          alias: formData.alias,
          title: titleField.translations,
          sections
        });
      }
      navigate('/pages');
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <AdminFormHeader
        title={page ? t('admin.pages.title.edit') : t('admin.pages.title.create')}
        subtitle={page ? t('admin.pages.subtitle.edit') : t('admin.pages.subtitle.create')}
      />
      <AdminForm
        leftColumn={
          <AdminFormSection title={t('admin.common.sections.basicInfo')}>
            <AdminInputText
              value={formData.alias}
              onChange={handleInputChange}
              name="alias"
              id="alias"
              label={t('admin.common.fields.alias')}
              required={true}
              error={errors.alias}
              disabled={!!id}
            />
            <AdminInputText
              label={t('admin.pages.fields.title')}
              value={titleField.currentValue}
              onChange={(e) => titleField.setCurrentValue(e.target.value)}
              name="title"
              id="title"
              required
              languageSwitcher={
                <FieldLanguageSwitcher
                  currentLanguage={titleField.currentLanguage}
                  onLanguageChange={titleField.setCurrentLanguage}
                  collapsed={true}
                  translatedLanguages={titleField.translatedLanguages}
                />
              }
            />
          </AdminFormSection>
        }
        rightColumn={
          <AdminFormSection title={t('admin.pages.sections.content')}>
            <AdminRichTextArea
              value={contentField.currentValue}
              onChange={contentField.setCurrentValue}
              name="content"
              id="content"
              label={t('admin.pages.sections.content')}
              error={errors.content}
              formats={['bold', 'italic', 'underline', 'script', 'link', 'list', 'header', 'indent']}
              languageSwitcher={
                <FieldLanguageSwitcher
                  currentLanguage={contentField.currentLanguage}
                  onLanguageChange={contentField.setCurrentLanguage}
                  collapsed={true}
                  translatedLanguages={contentField.translatedLanguages}
                />
              }
            />
          </AdminFormSection>
        }
        formActions={
          <>
            <AdminButton type="button" label={t('admin.common.actions.cancel')} onClick={() => navigate("/pages")}/>
            <AdminButton
              type="submit"
              label={page ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default PageForm;

